import { Color, Variations } from "@utilities/types";
import {
  forwardRef,
  useState,
  useImperativeHandle,
  useCallback,
  memo
} from "react";
import { Input } from "./Input";

export interface ColorPickerRef {
  resetColors: () => void;
}

interface ColorPickerProps {
  onChange: (colors: string[] | Color[]) => void;
  initialVariation?: Variations | null;
  initialColorsList?: Color[];
  withQuantity?: boolean;
}

const ColorPickerBase = forwardRef<ColorPickerRef, ColorPickerProps>(
    ({ onChange, initialVariation, initialColorsList, withQuantity = false }, ref ) => {

    const [colorInput, setColorInput] = useState("#000000");
    const [colors, setColors] = useState<Color[]>(
      // @ts-ignore
      initialVariation ? initialVariation.colors 
      : initialColorsList ? initialColorsList : []);

    const handleAddColor = useCallback(() => {
      if (colorInput.trim() && !colors.some(c => c.color === colorInput)) {
        const newColors = [
          ...colors,
          { color: colorInput, quantity: withQuantity ? 0 : undefined }
        ];
        setColors(newColors);
        onChange(withQuantity ? newColors : newColors.map(c => c.color));
      }
    }, [colorInput, colors, onChange, withQuantity]);

    const handleRemoveColor = useCallback((colorToRemove: string) => {
      const newColors = colors.filter((c) => c.color !== colorToRemove);
      setColors(newColors);
      onChange(withQuantity ? newColors : newColors.map(c => c.color));
    }, [colors, onChange, withQuantity]);

    const handleQuantityChange = useCallback((color: string, quantity: number) => {
      const updatedColors = colors.map(c =>
        c.color === color ? { ...c, quantity } : c
      );
      setColors(updatedColors);
      onChange(updatedColors);
    }, [colors, onChange]);

    const resetColors = useCallback(() => {
      setColors([]);
      setColorInput("#000000");
      onChange([]);
    }, [onChange]);

    useImperativeHandle(ref, () => ({
      resetColors
    }), [resetColors]);

    return (
      <div className="w-full p-4 border rounded-lg shadow-md bg-white">
        <label className="block text-gray-700 font-semibold mb-2">Couleurs disponibles</label>
        <div className="flex items-center gap-2 mb-3">
          <input
            type="color"
            value={colorInput}
            onChange={(e) => setColorInput(e.target.value)}
            className="w-12 h-12 p-1 border rounded-lg focus:outline-none"
          />
          <button
            onClick={handleAddColor}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg shadow-md hover:bg-blue-600 transition"
          >
            Ajouter une couleur
          </button>
        </div>
        <div className="flex flex-wrap gap-3">
          {colors.map(({ color, quantity }, index) => (
            <div key={index} className="flex items-center gap-2 p-2 border rounded-lg shadow-sm bg-gray-100">
              <span
                className="w-8 h-8 rounded-full border border-gray-300"
                style={{ backgroundColor: color }}
              ></span>
              {withQuantity && (
                <Input 
                  type="number" 
                  min={0}
                  className="input input-bordered w-16"
                  value={quantity || 0}
                  onChange={(e) =>
                    handleQuantityChange(color, parseInt(e.target.value) || 0)
                  }
                />
              )}
              <button
                onClick={() => handleRemoveColor(color)}
                className="text-red-500 hover:text-red-700 px-2 py-1 border border-red-300 rounded-lg"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

// ✅ Memo pour éviter les re-renders inutiles
export const ColorPicker = memo(ColorPickerBase);
